import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { User, Role, UpdateUserRequest } from '../../../../core/interfaces/user';
import { UsersApiService } from '../../../../core/services/users-api.service';
import { InputSelectComponent } from '../../../../shared/components/input-select/input-select.component';
import { InputTextComponent } from '../../../../shared/components/input-text/input-text.component';
import { MultiSelectInputComponent } from '../../../../shared/components/multi-select-input/multi-select-input.component';
import { BlockUIModule } from 'primeng/blockui';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AccountApiService } from '../../../../core/services/account-api.service';
import { SelectInput } from '../../../../shared/interfaces/select-input';
import { OrganizationApiService } from '../../../../core/services/organization-api.service';
import { requiredIfValidator } from '../../../../shared/validators/requiredIf-present.validator';
import { passwordLinkObserver } from '../../../../core/utils/password-link';

@Component({
  selector: 'app-edit-user',
  imports: [ButtonModule, 
            TableModule, 
            DialogModule, 
            InputTextComponent, 
            InputSelectComponent,
            ToastModule, 
            MultiSelectModule, 
            ReactiveFormsModule, 
            MultiSelectInputComponent,
            BlockUIModule,
            ProgressSpinnerModule],
  templateUrl: './edit-user.component.html',
  styleUrl: './edit-user.component.scss'
})
export class EditUserComponent {
  blockedDocument: boolean = false;
  @Input() visible!: boolean;
  accountOptions: SelectInput[] = [];
  @Output() closeDialog = new EventEmitter();
  organizationOptions: SelectInput[] = [];

  organizationLoaded = false;
  accountLoaded = false;

  _user!: User
  @Input() 
  set user(value: User) {
    this._user = value;
    if(value) {
      this._user = value;
      this.userForm.patchValue(this.user);
      this.userForm.controls.accountID.setValue(value.account?.id!)
      this.userForm.controls.organizationID.setValue(value.userOrganization?.organizationId!)
      this.userForm.controls.organizationTitle.setValue(value.userOrganization?.title!)      
    }

  }

  get user() {
    return this._user;
  }


  roles = [  {label: 'User', code: 'ROLE_USER'},
             {label: 'Admin', code: 'ROLE_ADMIN'}]
  statusOptions = [  {label: 'Yes', code: true},
             {label: 'No', code: false}]

  userForm = new FormGroup({
                   username: new FormControl('', [Validators.required, Validators.maxLength(50)]),
                   id: new FormControl('', Validators.required,),
                   email: new FormControl('', [Validators.required, Validators.email, Validators.maxLength(50)]),
                   organizationID: new FormControl('',),
                   organizationTitle: new FormControl('', Validators.maxLength(50)),
                   accountID: new FormControl('', Validators.required,),
                   roles: new FormControl<Role[]>([], Validators.required,),
                   active: new FormControl<boolean | undefined>(undefined, Validators.required,),
   },
                    {
                      validators: [requiredIfValidator('organizationTitle', 'organizationID','Organization'),]
                     });

  constructor(private messageService: MessageService, private userService: UsersApiService, 
    private accountApi: AccountApiService, private organizationApi: OrganizationApiService,
    private cdr: ChangeDetectorRef) {
    this.userForm.get('organizationID')!.valueChanges.subscribe(() => {
      this.userForm.get('organizationTitle')!.updateValueAndValidity();
    });

  }
  loadData(): void {
    this.loadAccounts()
    this.loadOrganizations();  
  }
  

             
  save() {
    if (this.userForm.valid) {
     const user: UpdateUserRequest = this.userForm.value as UpdateUserRequest
     this.blockedDocument = true;
     this.userService.updateUser(user).subscribe({
        next: (data) =>{
          this.blockedDocument = false;
          this.messageService.add({ severity: 'info', summary: 'Success', detail: 'User was successfully updated.' })
          this.close()
        } ,
        error: (err) => {
          this.blockedDocument = false;
          // 403 (outside the admin's organization, or a system administrator) gets the global "Permission denied" toast.
          if (err.status !== 403) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'A problem occurred during the user update.', life: 5000 })
          }
        }
     })
    } else {
        this.userForm.markAllAsTouched();
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please, verify the required fields.', life: 5000 })

    }

  }
  /** Admins can't type a password; they send the user a link instead (spec 004, FR-028). */
  sendPasswordLink() {
    this.userService.sendPasswordLink(this._user.id!).subscribe(passwordLinkObserver(this.messageService));
  }

  loadAccounts() {


    //if role_sys_admin load all accounts, else loads only user account
    if (this.isSysAdmin) {
             this.accountOptions = [    {
        "code": "",
        "label": "Select Account"
    },]
      this.accountApi.getAccounts().subscribe({
        next: (data) => {
          for(let item of data) {
            this.accountOptions.push({label: item.name, code: item.id})         
          }
          this.accountLoaded = true;
        }
        
      })
    }
  }
  close() {
    this.accountLoaded = false;
    this.organizationLoaded = false;
    this.closeDialog.emit("")
  }

  loadOrganizations() {

      this.organizationOptions = [    {
        "code": "",
        "label": "Select Organization"
       },]
      this.organizationApi.getOrganization().subscribe({
        next: (data) => {
          for(let item of data) {
            this.organizationOptions.push({label: item.name, code: item.id})         
          }
          this.organizationLoaded = true;
        }, error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: "It wasn't possible to load the accounts.", life: 5000 })
        }
        
      })
    

  }

    get isSysAdmin() {
      const userRoles = localStorage.getItem('roles')?.split(',');
      return userRoles?.includes("ROLE_SYS_ADMIN");
  }
}
